import { DiscordFilled, LockOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Tooltip,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import {
  DaoProjectItemType,
  DaoProposalActionType,
  DaoProposalApiType,
  DaoProposalDetailType,
  DaoProposalPayload,
  DaoProposalType,
  DaoProposalUpdateApiType,
} from 'Modules'
import { useEffect, useState } from 'react'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import MarkdownEditor from 'component/markdown-editor/markdown-editor'
import {
  DAO_DATETIME_FORMAT,
  DAO_MESSAGES,
  DAO_SALE_TIME_UNIT_OPTIONS,
  DaoProposalStatusEnum,
  DaoSaleTimeUnitEnum,
  DaoSellingVersionEnum,
  SALE_TIME_UNIT_TO_DAYJS,
} from 'constants/dao-voting'
import { useDaoVotingContract } from 'hooks/dao-voting/use-dao-voting-contract'
import { daoTxErrorMessage, usePreparedTx } from 'hooks/dao-voting/use-prepared-tx'
import { daoApiErrorMessage } from 'modules/dao-voting/utils'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logError } from 'utils/logger'

interface ProposalFormModalProps {
  open: boolean
  project: DaoProjectItemType
  /** present = edit mode (Draft or Scheduled) */
  proposal?: DaoProposalType | null
  onClose: () => void
  onSuccess: () => void
}

/** Telegram brand mark (blue circle + paper plane) - not in @ant-design/icons */
const TelegramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#229ED9" aria-label="Telegram">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
)

/** Facebook brand mark (blue circle + f) */
const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2" aria-label="Facebook">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
)

/** Zalo brand mark (blue rounded square + wordmark) - not in @ant-design/icons */
const ZaloIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-label="Zalo">
    <rect width="24" height="24" rx="5.4" fill="#0068FF" />
    <text
      x="12"
      y="15.8"
      textAnchor="middle"
      fill="#fff"
      fontSize="9.5"
      fontWeight="700"
      fontFamily="Arial, Helvetica, sans-serif"
    >
      Zalo
    </text>
  </svg>
)

/** Fixed discussion channels (mockup): one optional link per channel */
const DISCUSSION_CHANNELS = [
  {
    key: 'telegram',
    label: 'Telegram',
    placeholder: 'https://t.me/your-group-chat',
    match: ['t.me', 'telegram'],
    icon: <TelegramIcon />,
  },
  {
    key: 'facebook',
    label: 'Facebook',
    placeholder: 'https://facebook.com/groups/...',
    match: ['facebook.com', 'fb.com', 'fb.me'],
    icon: <FacebookIcon />,
  },
  {
    key: 'discord',
    label: 'Discord',
    placeholder: 'https://discord.gg/...',
    match: ['discord.gg', 'discord.com'],
    icon: <DiscordFilled style={{ color: '#5865F2' }} />,
  },
  {
    key: 'zalo',
    label: 'Zalo',
    placeholder: 'https://zalo.me/g/...',
    match: ['zalo.me', 'zalo'],
    icon: <ZaloIcon />,
  },
]

/** BE stores a flat array - best-effort place each link into its channel slot */
const toChannelSlots = (links?: string[] | null): (string | undefined)[] => {
  const slots: (string | undefined)[] = DISCUSSION_CHANNELS.map(() => undefined)
  const unmatched: string[] = []
  ;(links || []).forEach((link) => {
    const index = DISCUSSION_CHANNELS.findIndex(
      (channel, i) => !slots[i] && channel.match.some((host) => link.includes(host))
    )
    if (index >= 0) slots[index] = link
    else unmatched.push(link)
  })
  unmatched.forEach((link) => {
    const free = slots.findIndex((slot) => !slot)
    if (free >= 0) slots[free] = link
  })
  return slots
}

const percentRules = (field: string, min = 1) => [
  { required: true, message: `${field} is required` },
  {
    validator: (_: unknown, value?: number) => {
      if (value === undefined || value === null) return Promise.resolve()
      if (value < min) return Promise.reject(new Error(DAO_MESSAGES.E01(field, min)))
      if (value > 100) return Promise.reject(new Error(DAO_MESSAGES.E02(field)))
      return Promise.resolve()
    },
  },
]

/**
 * Create Proposal / Edit Proposal (SRS 5.3).
 * Create & Draft edit: Cancel / Save as Draft / Publish Proposal.
 * Scheduled edit: Cancel / Save Changes.
 */
const ProposalFormModal = ({
  open,
  project,
  proposal,
  onClose,
  onSuccess,
}: ProposalFormModalProps) => {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  // edit mode: block submits until the detail API has hydrated the form,
  // otherwise saving would wipe the fields the list API does not return
  const [detailLoaded, setDetailLoaded] = useState(false)
  // detail also carries onChainProposalId/contractAddress needed for on-chain update
  const [proposalDetail, setProposalDetail] = useState<DaoProposalDetailType | null>(null)

  // live values driving the Timeline Preview (SRS 5.3)
  const votingStartValue = Form.useWatch('votingStart', form)
  const votingEndValue = Form.useWatch('votingEnd', form)
  const estimatedSaleTimeValue = Form.useWatch('estimatedSaleTime', form)
  const estimatedSaleTimeUnitValue = Form.useWatch('estimatedSaleTimeUnit', form)

  const { address } = useAccount()
  const { proposeOnChain, updateProposalOnChain } = useDaoVotingContract()
  const { sendPreparedTx } = usePreparedTx()

  // not-deployed (no contract yet, e.g. the propose tx failed) edits like a
  // Draft so the admin can re-publish and retry the on-chain step.
  // "Save Changes" mode is only for a Scheduled proposal that IS on-chain.
  const isDeployed = !!proposal?.contractAddress
  const isEditScheduled = proposal?.status === DaoProposalStatusEnum.SCHEDULED && isDeployed
  // mid-publish: the record content is already fixed - only the voting
  // schedule may still be adjusted before re-publishing
  const isPublishing = proposal?.status === DaoProposalStatusEnum.PUBLISHING
  const canSubmit = !proposal || detailLoaded

  // SRS 3.2 #6: max 1 Active/Scheduled proposal per project - while another
  // proposal is running (or one is mid-publish / mid-delete - still Pending
  // on-chain), a Draft can only be saved
  const hasBlockingProposal = (project.proposals || []).some(
    (item) =>
      item._id !== proposal?._id &&
      (item.status === DaoProposalStatusEnum.ACTIVE ||
        item.status === DaoProposalStatusEnum.SCHEDULED ||
        item.status === DaoProposalStatusEnum.PUBLISHING ||
        item.status === DaoProposalStatusEnum.DELETING)
  )

  useEffect(() => {
    if (!open) return
    form.resetFields()
    setDetailLoaded(false)
    setProposalDetail(null)
    if (!proposal) return
    form.setFieldsValue({
      title: proposal.title,
      description: proposal.description,
      terms: proposal.terms,
      discussionLinks: toChannelSlots(proposal.discussionLinks),
      votingStart: proposal.votingStart ? dayjs(proposal.votingStart) : undefined,
      votingEnd: proposal.votingEnd ? dayjs(proposal.votingEnd) : undefined,
      quorum: proposal.quorum,
      threshold: proposal.threshold,
      estimatedSaleTime: proposal.estimatedSaleTime,
      estimatedSaleTimeUnit: proposal.estimatedSaleTimeUnit ?? DaoSaleTimeUnitEnum.DAYS,
    })

    // Draft / Publishing / not-deployed: the proposals list already carries
    // the full content and there are no on-chain ids to look up - no detail call
    if (
      proposal.status === DaoProposalStatusEnum.DRAFT ||
      proposal.status === DaoProposalStatusEnum.PUBLISHING ||
      !proposal.contractAddress
    ) {
      setDetailLoaded(true)
      return
    }

    // Deployed Scheduled: detail also brings onChainProposalId/contractAddress
    // for the on-chain updateProposal step
    let cancelled = false
    AppAdmin.getDaoVotingProposalDetail(proposal._id)
      .then((res) => {
        const detail = res.data?.data
        if (cancelled || !detail) return
        setProposalDetail(detail)
        form.setFieldsValue({
          title: detail.title,
          description: detail.description,
          terms: detail.termsAndConditions,
          discussionLinks: toChannelSlots(detail.discussionRoomLinks),
          votingStart: detail.votingStartDate ? dayjs(detail.votingStartDate) : undefined,
          votingEnd: detail.votingEndDate ? dayjs(detail.votingEndDate) : undefined,
          quorum: detail.quorumPercent ?? undefined,
          threshold: detail.thresholdPercent ?? undefined,
          estimatedSaleTime: detail.estimatedSaleTime ?? undefined,
          estimatedSaleTimeUnit: detail.estimatedSaleTimeUnit ?? DaoSaleTimeUnitEnum.DAYS,
        })
        setDetailLoaded(true)
      })
      .catch((error) => {
        if (cancelled) return
        logError('Error fetching proposal detail: ', error)
        message.error('Failed to load proposal detail')
      })
    return () => {
      cancelled = true
    }
  }, [open, proposal, form])

  const toPayload = (values: any, action: DaoProposalActionType): DaoProposalPayload => ({
    title: (values.title || '').trim(),
    description: values.description,
    termsAndConditions: values.terms,
    discussionRoomLinks: (values.discussionLinks || []).filter(
      (link: string | undefined) => !!link?.trim()
    ),
    votingStartDate: values.votingStart ? (values.votingStart as Dayjs).toISOString() : undefined,
    votingEndDate: values.votingEnd ? (values.votingEnd as Dayjs).toISOString() : undefined,
    quorumPercent: values.quorum,
    thresholdPercent: values.threshold,
    estimatedSaleTime: values.estimatedSaleTime,
    estimatedSaleTimeUnit: values.estimatedSaleTimeUnit,
    action,
  })

  /** POST when creating, PATCH when editing an existing Draft/Scheduled proposal */
  const submitProposal = async (
    payload: DaoProposalPayload
  ): Promise<DaoProposalApiType | DaoProposalUpdateApiType | undefined> => {
    if (proposal?._id) {
      const res = await AppAdmin.updateDaoVotingProposal(proposal._id, payload)
      return res.data?.data
    }
    const res = await AppAdmin.createDaoVotingProposal({ ...payload, tokenize: project._id })
    return res.data?.data
  }

  const handlePublish = async () => {
    try {
      const values = await form.validateFields()
      if (!address) {
        message.error('Please connect wallet!')
        return
      }
      setSubmitting(true)

      // PUBLISHING: if the previous propose tx already landed and the BE is
      // verifying it, do NOT send another on-chain propose
      if (isPublishing && proposal?._id) {
        const detailRes = await AppAdmin.getDaoVotingProposalDetail(proposal._id)
        if (detailRes.data?.data?.onChainVerified) {
          message.info('This proposal is already on-chain and being verified. Please wait.')
          onSuccess()
          onClose()
          return
        }
      }

      // 1. BE creates the record and prepares the proposeWithParams args.
      //    A PUBLISHING proposal only re-adjusts its schedule (UPDATE_TIME)
      const created = await submitProposal(
        toPayload(values, isPublishing ? 'UPDATE_TIME' : 'PUBLISH')
      )

      // 2. Manager wallet sends the on-chain propose tx (DAO_Usage_Guide 3.2)
      const proposeCall = created?.onChainProposeCall
      if (proposeCall) {
        try {
          onOpenModalProcessing()
          await proposeOnChain(address, proposeCall)
        } catch (chainError) {
          // BE record already exists - surface the on-chain failure explicitly
          logError('Error publishing proposal on-chain: ', chainError)
          message.error(
            daoTxErrorMessage(chainError) || 'Proposal saved but on-chain publish failed'
          )
          onSuccess()
          onClose()
          return
        } finally {
          onCloseModalProcessing()
        }
      }

      message.success(DAO_MESSAGES.S01)
      onSuccess()
      onClose()
    } catch (error: any) {
      if (error?.errorFields) return
      logError('Error publishing proposal: ', error)
      message.error(daoApiErrorMessage(error) || 'Failed to publish proposal')
      // PUBLISHING: the BE may have moved the proposal on meanwhile (verified /
      // reverted) - reload the proposals list so the row shows the latest status
      if (isPublishing) onSuccess()
    } finally {
      setSubmitting(false)
    }
  }

  const handleSaveChanges = async () => {
    try {
      const values = await form.validateFields()
      if (!address) {
        message.error('Please connect wallet!')
        return
      }
      setSubmitting(true)

      // 1. BE updates the record
      const res = await AppAdmin.updateDaoVotingProposal(proposal!._id, toPayload(values, 'SAVE'))
      const updated = res.data?.data

      // 2. Scheduled proposal lives on-chain (Pending) - push the new schedule
      //    and thresholds via updateProposal (DAO_Usage_Guide 3.2).
      //    PTO ERC-3643: the BE returns the encoded tx - send it as is.
      //    PTO NFT: PATCH returns a slim record, so on-chain ids come from the
      //    detail loaded when the edit form opened; args come from the form values.
      const updateCall = updated?.onChainUpdateCall
      const proposeCall = updated?.onChainProposeCall
      const onChainProposalId =
        updated?.onChainProposalId ||
        proposeCall?.onChainProposalId ||
        proposalDetail?.onChainProposalId
      const contractAddress = proposeCall?.contractAddress || proposalDetail?.contractAddress
      let sendUpdate: (() => Promise<unknown>) | null = null
      if (updateCall?.to && updateCall?.data) {
        sendUpdate = () => sendPreparedTx(address, updateCall)
      } else if (onChainProposalId && contractAddress) {
        const params = proposeCall?.proposalParams
        sendUpdate = () =>
          updateProposalOnChain(address, {
            contractAddress,
            onChainProposalId,
            snapshot:
              params?.snapshot ?? Math.floor((values.votingStart as Dayjs).valueOf() / 1000),
            deadline: params?.deadline ?? Math.floor((values.votingEnd as Dayjs).valueOf() / 1000),
            totalUnits: params?.totalUnits ?? project.totalSupply,
            quorumBps: params?.quorumBps ?? (values.quorum || 0) * 100,
            thresholdBps: params?.thresholdBps ?? (values.threshold || 0) * 100,
          })
      }
      if (sendUpdate) {
        try {
          onOpenModalProcessing()
          await sendUpdate()
        } catch (chainError) {
          logError('Error updating proposal on-chain: ', chainError)
          message.error(
            daoTxErrorMessage(chainError) || 'Proposal saved but on-chain update failed'
          )
          onSuccess()
          onClose()
          return
        } finally {
          onCloseModalProcessing()
        }
      }

      message.success(DAO_MESSAGES.S03)
      onSuccess()
      onClose()
    } catch (error: any) {
      if (error?.errorFields) return
      logError('Error updating proposal: ', error)
      message.error(daoApiErrorMessage(error) || 'Failed to update proposal')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSaveDraft = async () => {
    try {
      // Draft: only Title is required; other fields keep their own rules if filled
      await form.validateFields(['title'])
      const values = form.getFieldsValue()
      setSubmitting(true)
      await submitProposal(toPayload(values, 'DRAFT'))
      message.success(DAO_MESSAGES.S02)
      onSuccess()
      onClose()
    } catch (error: any) {
      if (error?.errorFields) return
      logError('Error saving draft: ', error)
      message.error(daoApiErrorMessage(error) || 'Failed to save draft')
    } finally {
      setSubmitting(false)
    }
  }

  /** Timeline Preview (SRS 5.3) - live per Voting Start/End + Estimated Sale Time */
  const fmtPreview = (value?: Dayjs | null) =>
    value ? dayjs(value).format(DAO_DATETIME_FORMAT) : ''
  const saleEndPreview =
    votingEndValue && estimatedSaleTimeValue && estimatedSaleTimeValue >= 1
      ? dayjs(votingEndValue).add(
          estimatedSaleTimeValue,
          SALE_TIME_UNIT_TO_DAYJS[
            (estimatedSaleTimeUnitValue as DaoSaleTimeUnitEnum) || DaoSaleTimeUnitEnum.DAYS
          ]
        )
      : null
  const previewSteps = [
    { title: 'Published onchain', sub: 'at publish', set: true },
    {
      title: 'Voting period started',
      sub: votingStartValue ? fmtPreview(votingStartValue) : 'Set a voting start',
      set: !!votingStartValue,
    },
    {
      title: 'Voting period ended',
      sub: votingEndValue ? fmtPreview(votingEndValue) : 'Set a voting end',
      set: !!votingEndValue,
    },
    {
      title: 'Proposal result',
      sub: votingEndValue
        ? `Passed / Rejected at ${fmtPreview(votingEndValue)}`
        : 'Passed / Rejected at voting end',
      set: !!votingEndValue,
    },
    {
      title: 'Asset sale period',
      sub: saleEndPreview
        ? `Est. ${fmtPreview(votingEndValue)} – ${fmtPreview(saleEndPreview)}`
        : 'Set voting end and estimated sale time',
      set: !!saleEndPreview,
    },
    { title: 'Sale result', sub: 'Date TBD', set: false },
    { title: 'Claims open', sub: 'Date TBD', set: false },
    { title: 'Claim period ended', sub: 'Date TBD', set: false },
  ]

  // Button order per mockup: Cancel / Publish Proposal / Save as Draft
  const footer = [
    <Button key="cancel" onClick={onClose}>
      Cancel
    </Button>,
    ...(isEditScheduled
      ? [
          <Button
            key="save"
            type="primary"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSaveChanges}
          >
            Save Changes
          </Button>,
        ]
      : [
          // publishing is blocked while the project already has an
          // Active/Scheduled proposal (SRS 3.2 #6)
          ...(!hasBlockingProposal
            ? [
                <Button
                  key="publish"
                  type="primary"
                  className="dao-view-detail-btn"
                  loading={submitting}
                  disabled={!canSubmit}
                  onClick={handlePublish}
                >
                  Publish Proposal
                </Button>,
              ]
            : []),
          // already-published proposal (re-publish of a not-deployed one)
          // cannot go back to Draft - only create/edit-draft offers it
          ...(!proposal || proposal.status === DaoProposalStatusEnum.DRAFT
            ? [
                <Button
                  key="draft"
                  style={{ borderColor: '#d4b106', color: '#d4b106' }}
                  loading={submitting}
                  disabled={!canSubmit}
                  onClick={handleSaveDraft}
                >
                  Save as Draft
                </Button>,
              ]
            : []),
        ]),
  ]

  return (
    <Modal
      title={
        <div>
          <div>{proposal ? 'Edit Proposal' : 'Create Proposal'}</div>
          <div style={{ fontSize: 13, fontWeight: 400, color: 'rgba(0,0,0,0.55)' }}>
            {proposal ? 'Edit' : 'New'} DAO governance proposal for {project.fullName}
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      width={720}
      maskClosable={false}
      destroyOnClose
      className="dao-proposal-form"
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Linked PTO Project">
          <Input value={project.fullName} disabled suffix={<LockOutlined />} />
        </Form.Item>

        <Form.Item
          label="Proposal Title"
          name="title"
          rules={[{ required: true, message: 'Proposal Title is required' }]}
        >
          <Input
            placeholder="e.g. Approve Sale Price of $2.1M"
            maxLength={255}
            disabled={isPublishing}
          />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[
            { required: true, message: 'Description is required' },
            {
              validator: (_, value?: string) =>
                value && value.length > 3000
                  ? Promise.reject(new Error('Description cannot be longer than 3000 characters'))
                  : Promise.resolve(),
            },
          ]}
        >
          <MarkdownEditor
            placeholder="Explain what token holders are voting on"
            disabled={isPublishing}
            height={180}
          />
        </Form.Item>

        <Form.Item
          label="Terms & Conditions"
          name="terms"
          rules={[{ required: true, message: 'Terms & Conditions is required' }]}
        >
          <MarkdownEditor
            placeholder="State the binding terms holders accept by voting — closing conditions, fees, distribution rules"
            disabled={isPublishing}
            height={180}
          />
        </Form.Item>

        {/* mockup: one optional link per fixed channel */}
        <Form.Item
          label={
            <>
              Links to the Discussion Room
              <span style={{ marginLeft: 6, fontWeight: 400, color: 'rgba(0,0,0,0.45)' }}>
                (optional)
              </span>
            </>
          }
          extra="Public group chats where holders can discuss this proposal. Leave blank any channel you don't use."
          style={{ marginBottom: 12 }}
        >
          {DISCUSSION_CHANNELS.map((channel, index) => (
            <div
              key={channel.key}
              style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  background: '#fafafa',
                  fontSize: 16,
                  flexShrink: 0,
                }}
              >
                {channel.icon}
              </div>
              <Form.Item
                name={['discussionLinks', index]}
                style={{ flex: 1, marginBottom: 0 }}
                rules={[
                  { type: 'url', message: 'Please enter a valid link' },
                  {
                    validator: (_, value?: string) => {
                      // empty = channel not used; wrong-format is caught by the url rule
                      if (!value?.trim()) return Promise.resolve()
                      return channel.match.some((host) => value.includes(host))
                        ? Promise.resolve()
                        : Promise.reject(new Error(`Please enter a valid ${channel.label} link`))
                    },
                  },
                ]}
              >
                <Input placeholder={channel.placeholder} maxLength={3000} disabled={isPublishing} />
              </Form.Item>
            </div>
          ))}
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Voting Start"
              name="votingStart"
              rules={[
                { required: true, message: 'Voting Start is required' },
                {
                  validator: (_, value?: Dayjs) => {
                    // block past time compared to publish moment (SRS DAO_E04)
                    if (value && value.valueOf() < Date.now() && !isEditScheduled) {
                      return Promise.reject(new Error(DAO_MESSAGES.E04))
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <DatePicker showTime style={{ width: '100%' }} format="MMM DD, YYYY, h:mm A" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Voting End"
              name="votingEnd"
              dependencies={['votingStart']}
              rules={[
                { required: true, message: 'Voting End is required' },
                ({ getFieldValue }) => ({
                  validator: (_, value?: Dayjs) => {
                    const start: Dayjs | undefined = getFieldValue('votingStart')
                    if (value && start && value.valueOf() <= start.valueOf()) {
                      return Promise.reject(new Error(DAO_MESSAGES.E03))
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <DatePicker showTime style={{ width: '100%' }} format="MMM DD, YYYY, h:mm A" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <>
                  Quorum %
                  <Tooltip title="The minimum threshold of voting participation required for a DAO proposal to be considered valid">
                    <QuestionCircleOutlined style={{ marginLeft: 6 }} />
                  </Tooltip>
                </>
              }
              name="quorum"
              // PTO ERC-3643 accepts 0% = no quorum required
              rules={percentRules(
                'Quorum',
                project.sellingVersion === DaoSellingVersionEnum.ERC3643 ? 0 : 1
              )}
            >
              <InputNumber
                placeholder="e.g. 30"
                style={{ width: '100%' }}
                disabled={isPublishing}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <>
                  Threshold %
                  <Tooltip title="The minimum required support for a DAO proposal to pass">
                    <QuestionCircleOutlined style={{ marginLeft: 6 }} />
                  </Tooltip>
                </>
              }
              name="threshold"
              rules={percentRules('Threshold')}
            >
              <InputNumber
                placeholder="e.g. 51"
                style={{ width: '100%' }}
                disabled={isPublishing}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Estimated Sale Time"
          required
          extra="How long the real-world sale is expected to take after the proposal passes. Shown to holders as an estimate."
        >
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="estimatedSaleTime"
              style={{ flex: 1, marginBottom: 0 }}
              rules={[
                { required: true, message: 'Estimated Sale Time is required' },
                {
                  validator: (_, value?: number) =>
                    value != null && value < 1
                      ? Promise.reject(new Error(DAO_MESSAGES.E06('Estimated Sale Time')))
                      : Promise.resolve(),
                },
              ]}
            >
              {/* off-chain estimate (timeline milestone only) - stays editable
                  in every edit context, including mid-publish */}
              <InputNumber placeholder="e.g. 3" style={{ width: '100%' }} min={1} precision={0} />
            </Form.Item>
            <Form.Item
              name="estimatedSaleTimeUnit"
              style={{ width: 140, marginBottom: 0 }}
              initialValue={DaoSaleTimeUnitEnum.DAYS}
            >
              <Select options={DAO_SALE_TIME_UNIT_OPTIONS} />
            </Form.Item>
          </div>
        </Form.Item>

        {/* Timeline Preview (SRS 5.3) - read-only, updates live; (6)-(8) stay TBD */}
        <div className="dao-timeline-preview">
          <div className="dao-timeline-preview-title">Timeline Preview</div>
          {previewSteps.map((step, index) => (
            <div key={step.title} className={`dao-timeline-preview-item ${step.set ? 'set' : ''}`}>
              <span className="dao-timeline-preview-dot">({index + 1})</span>
              <div>
                <div className="dao-timeline-preview-step">{step.title}</div>
                <div className="dao-timeline-preview-sub">{step.sub}</div>
              </div>
            </div>
          ))}
          <div className="dao-timeline-preview-caption">
            Steps (5)–(8) are shown to holders only if the proposal passes.
          </div>
        </div>
      </Form>
    </Modal>
  )
}

export default ProposalFormModal
